<!--

============================================================================================================
||    DraggableList component                                                                           ||
============================================================================================================

|   Description
------------------------------------------------------------------------------------------------------------
Component is used for lists that can be sorted via drag handle on the right.

|   Features
------------------------------------------------------------------------------------------------------------
  - Drag handle
  - Delete button
  - on/off toggle
  - clickable
  - prefix icon/image
  - label is editable

|   Props
------------------------------------------------------------------------------------------------------------
  - disable         : Boolean    : Dragging is not available.
  - no-delete       : Boolean    : Delete button is not available.
  - no-toggle       : Boolean    : Toggle is not available.
  - toggle-tooltip  : String     : Tooltip for the toggle. Provide only the first part of the sentence. ON/OFF will be added automatically to the end. e.g. "Flow is turned" will become "Flow is turned ON/OFF".
  - edit            : Boolean    : Label is editable.
  - mark-selected   : Boolean    : Selected item is marked.
  - pointer         : Boolean    : Cursor is pointer.

|   Events
------------------------------------------------------------------------------------------------------------
  - click - When user clicks on the item.

|   Slots
------------------------------------------------------------------------------------------------------------
  - right - To add custom button to the right.
  - prefix - To add custom content before the item icon and text.
  - suffix - To add custom content after the item text.

|   TODO
------------------------------------------------------------------------------------------------------------
  - IDEA - Enable dragging by the whole item, not just the drag handle as an optional feature.
  - Slots

|   v-model(value) format
------------------------------------------------------------------------------------------------------------
  [
    {
      id: Number|String,
      label: String,
      icon: {
        icon: SBIconList Object,
        color: String,
      },
      hide: boolean,
      static: boolean,
      active: boolean,
      error: String,
      [anyOtherParameter]: 'Can be included but will be ignored',
    },
    ...
  ]

-->

<template>
  <draggable
    v-model="listItems"
    class="drag-wrapper"
    :class="{ disabled: disable, dragging: draggingInProgress }"
    handle=".sort-handler"
    draggable=".item-wrapper"
    swap-threshold="1"
    :animation="150"
    @choose="draggingInProgress = true"
    @unchoose="draggingEndWithDelay"
    @end="handleItemSort" >
    <div
      v-for="(lItem, index) in listItems"
      :key="index"
      class="item-wrapper"
      :class="{ hide: lItem.hide }" >
      <span
        class="sort-handler" >
        <span class="drag-icon" />
      </span>
      <div
        :class="{
          selected: markSelected && selectedItem.id === lItem.id,
          static: lItem.static,
          pointer,
        }"
        class="item flex center-all"
        @click="selectItem(lItem, edit)" >
        <!-- Prefix icon/image -->
        <CircleSpinner
          v-if="updatingItems.includes(lItem.uid)"
          class="mr-2"
          :diameter="14"
          :progress="0"
          infinite
          :stroke-width="2"
          background="#CFD8DC" />
        <SBIcon
          v-else-if="!hideImage && lItem.icon"
          class="mr-2"
          :icon="lItem.icon.icon"
          :color="lItem.icon.color || SBColors.greyPrimary"
          :size="14" />
        <img
          v-else-if="!hideImage && lItem.image"
          :src="lItem.image"
          height="14px"
          width="14px"
          class="mr-2">
        <!-- Needed in case some items have an image and some don't. This spacer lines up the text vertically for all the items. -->
        <div
          v-else-if="!hideImage"
          class="no-image-spacer" />
        <div
          v-if="lItem.integration"
          class="integration-icon mr-2"
          :class="[lItem.integration]" />
        <!-- Label input -->
        <b-input
          v-if="edit"
          :ref="`label-input-${lItem.id}`"
          v-model="lItem.label"
          :class="{active: selectedItem.id === lItem.id}"
          class="label-input ellipsis"
          placeholder="Enter label"
          @keydown.exact.esc.native="quitNewLabel(lItem)"
          @keydown.exact.enter.native="updateLabel(lItem)"
          @focusout.native="updateLabel(lItem)" />
        <!-- Label text -->
        <span
          v-else
          class="greedy">{{ lItem.label }}</span>
        <!-- Error flag -->
        <SBIcon
          v-if="!updatingItems.includes(lItem.uid) && lItem.errorFlag"
          class="ml-1"
          :icon="SBIconList.TRIANGLE_EXCLAMATION"
          :color="SBColors.redPrimary"
          :size="12" />
        <!-- Toggle switch -->
        <div
          v-if="enableToggle"
          :id="`switch-${lItem.id}`">
          <SwitchCheckbox
            v-model="lItem.active"
            size="mini-small"
            class="no-label-margin"
            :off-outline-color="SBColors.blackUltralight"
            :value="lItem.active"
            @click.native.stop
            @input="updateItemStatus(lItem)" />
        </div>
        <b-popover
          :target="`switch-${lItem.id}`"
          triggers="hover"
          placement="bottom"
          custom-class="basic-popover"
          :delay="{ show: 200, hide: 0 } " >
          {{ toggleTooltip }} {{ lItem.active ? 'ON' : 'OFF' }}
        </b-popover>
      </div>
      <div
        v-if="lItem.error"
        class="error flex flex-align-center">
        <SBIcon
          class="mr-2"
          :icon="SBIconList.ERROR"
          :color="SBColors.redPrimary"
          :size="12" />
        <span>{{ lItem.error }}</span>
      </div>
      <SBIcon
        v-if="!lItem.static"
        class="on-parent-hover trash"
        :icon="SBIconList.TRASH"
        :color="SBColors.blackUltralight"
        :size="10"
        :hover-size="22"
        :pointer="true"
        :border-radius="30"
        :hover-icon-color="SBColors.redPrimary"
        @click.native.stop
        @click="deleteItem(lItem)" />
    </div>
  </draggable>
</template>

<script>
import draggable from 'vuedraggable';
import SwitchCheckbox from '@/components/common/SwitchCheckbox';
import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import CircleSpinner from '@/components/animatedIcons/CircleSpinner';

export default {
  name: 'DraggableList',
  components: {
    CircleSpinner,
    draggable,
    SwitchCheckbox,
    SBIcon,
  },
  props: {
    value: {
      type: Array,
      required: true,
    },
    disable: {
      type: Boolean,
      default: false,
    },
    noDelete: {
      type: Boolean,
      default: false,
    },
    noToggle: {
      type: Boolean,
      default: false,
    },
    toggleTooltip: {
      type: String,
      required: false,
      default: 'Turned',
    },
    edit: {
      type: Boolean,
      default: false,
    },
    markSelected: {
      type: Boolean,
      default: false,
    },
    pointer: {
      type: Boolean,
      default: false,
    },
    enableToggle: {
      type: Boolean,
      required: false,
      default: true,
    },
    updatingItems: {
      type: Array,
      required: false,
      default: () => [],
    },
    hideImage: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      draggingInProgress: false,
      selectedItem: {},
      listItems: [],
      SBIconList,
      SBColors,
    };
  },
  computed: {},
  watch: {
    value: {
      handler() {
        this.listItems = [...this.value];
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    draggingEndWithDelay() {
      setTimeout(() => {
        this.draggingInProgress = false;
      }, 0);
    },
    handleItemSort(data) {
      this.$emit('input', this.listItems);
      this.$emit('reorder', { newIndex: data.newIndex, oldIndex: data.oldIndex, item: this.listItems[data.newIndex] });
    },
    selectItem(item, focus) {
      // console.log('select item');
      this.selectedItem = item;
      if (focus) {
        this.$refs[`label-input-${this.selectedItem.id}`][0].focus();
      }
      this.$emit('selected', item);
    },
    quitNewLabel() {
      // console.log('quit new response');
    },
    updateLabel() {
      // console.log('update label');
    },
    updateItemStatus(item) {
      // console.log('update reply status');
      this.$emit('activate', item);
    },
    deleteItem(item) {
      this.$emit('remove', item);
    },
  },
};
</script>

<style lang="scss" scoped>
.drag-wrapper {
  margin-top: 12px;
  margin-bottom: 33px;

  .item-wrapper {
    margin: 0 0 12px;
    padding-right: 40px;
    padding-left: 40px;
    position: relative;

    .sort-handler {
      display: none;
      position: absolute;
      top: calc(50% - 12px);
      left: 12px;
      cursor: pointer;
      width: 18px;
      height: 24px;
      border-radius: 5px;
      box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.1);
      border: solid 1px $greyBorder;
      overflow: hidden;

      .drag-icon {
        display: block;
        height: 100%;
        width: 100%;
        mask: url('~@/assets/img/SB-Icons/drag.svg') no-repeat center !important;
        background-color: $blackUltralight;
      }

      &:hover {
        box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.3);

        .drag-icon {
          background-color: $blackPrimary;
        }
      }
    }

    &:hover .sort-handler {
      display: block;
    }

    .sb-icon.on-parent-hover {
      display: none;
      position: absolute;
      top: 6px;
      right: 8px;
      box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.1);
      border: solid 1px $greyBorder;
    }

    &:hover .sb-icon.on-parent-hover {
      display: flex;

      &:hover {
        box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.3);
      }
    }

    &.sortable-ghost {
      &::before {
        content: '';
        position: absolute;
        display: block;
        top: 0;
        bottom: 0;
        left: 40px;
        right: 40px;
        background: #F8FAFB;
        opacity: 0.7;
        border-radius: 5px;
      }

      .item {
        opacity: 0 !important;
      }
    }

    .item {
      padding: 0 10px;
      border-radius: 5px;
      box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.1);
      border: solid 1px $greyBorder;
      height: 32px;
      background: $white;

      .no-image-spacer {
        width: 22px;
      }

      * {
        font-size: 14px;
      }

      &:hover {
        box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.3);
      }

      &.pointer:hover {
        cursor: pointer;
      }

      &.selected {
        border: solid 1px $blackPrimary;
      }

      &:first-child {
        margin-top: 0;
      }

      .integration-icon {
        width: 14px;
        height: 14px;
        background-size: contain !important;
        background-repeat: no-repeat;
      }

      .response-keyword {
        font-size: 14px;
        font-weight: 400;
        color: $blackPrimary;
        min-height: 20px;
      }

      .label-input {
        cursor: pointer;
        padding: 0;
        border: none;
        box-shadow: none;
        margin-right: 10px;
        height: 30px;

        &:focus {
          border: none !important;
          box-shadow: none !important;
        }
      }

      &.static .response-keyword {
        font-weight: 500;
      }

      &.hide {
        display: none !important;
      }
    }
  }

  &.disabled {
    .sort-handler {
      display: none !important;
    }
  }

  &.dragging {
    .sb-icon.on-parent-hover {
      display: none !important;
    }

    .item-wrapper:not(.sortable-chosen) {
      .sort-handler {
        display: none !important;
      }
    }

    .item-wrapper:not(.sortable-chosen) {
      .sort-handler {
        display: none !important;
      }
    }
  }
}
</style>
